'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { BoostDialog } from '@/components/market/boost-dialog';
import { MarketBoostersSummary } from '@/components/market/boosters/market-boosters-summary';
import { PoolShareRow } from '@/components/market/pool-share-row';
import { AnimatedD6 } from '@/components/market/position-card';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { CardSectionLabel } from '@/components/ui/card-section-label';
import { Icon } from '@/components/ui/icon';
import { Award01IconSolid, HelpCircleIconSolid, Rocket01IconSolid } from '@/components/ui/icon/glyphs';
import { D6_SCALE } from '@/config/solana';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { formatPercentTrim } from '@/lib/chart/format';
import { useFormattedCurrency } from '@/lib/currency';
import { canBoostMarket } from '@/lib/market/can-boost-market';
import { cn } from '@/lib/ui/utils';
import { usePrizePoolContext } from './prize-pool-context';
export interface PrizePoolCardProps {
  /** Market pubkey — required for the Boost dialog trigger. */
  marketPubkey: string;
  /** Market lifecycle status — gates the Boost action so it is hidden on
   *  non-tradeable markets (resolved / graduated / pre-trade) where boosting
   *  errors out at the contract (MOR-735). */
  marketStatus: string;
  className?: string;
  boosters?: React.ReactNode;
}

/**
 * Prize Pool hero card — the single visual surface for the prize pool on the
 * market detail page. Renders one of four stages derived by the provider:
 *
 *   - `loading`           — public amount + always-safe actions while viewer positions resolve
 *   - `pre-finalization` — header badge + amount + Info & Boost buttons
 *   - `finalized-winner`  — header badge + share/total + Info + boosters, then Claim CTA
 *   - `finalized-loser`   — Prize Pool label + total + Info
 *
 * Layout (Figma node 3890:1728): left-aligned, with a trophy + "PRIZE POOL"
 * header badge, a left-aligned amount, and the action buttons grouped at the
 * bottom-left. A soft glow decorates the top-right corner. Provider state is
 * unchanged in shape — `state.stage` is the only switch the card branches on.
 */
export function PrizePoolCard(t0) {
  const $ = _c(80);
  const {
    marketPubkey,
    marketStatus,
    className,
    boosters
  } = t0;
  let t1;
  if ($[0] !== boosters || $[1] !== marketPubkey || $[2] !== marketStatus) {
    t1 = boosters === undefined ? <MarketBoostersSummary marketPubkey={marketPubkey} marketStatus={marketStatus} /> : boosters;
    $[0] = boosters;
    $[1] = marketPubkey;
    $[2] = marketStatus;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const boostersContent = t1;
  const t = useTranslations("chart.prize_pool");
  const tMarket = useTranslations("market");
  const {
    formatBalance,
    locale
  } = useFormattedCurrency();
  const {
    state,
    actions: t2
  } = usePrizePoolContext();
  const {
    onClaim,
    setHelpModalOpen
  } = t2;
  const {
    stage,
    amount,
    userShareD6,
    userSharePct,
    payoutEstimates,
    payoutEstimatesPending,
    claimed,
    claimInFlight
  } = state;
  let t3;
  if ($[4] !== userShareD6) {
    t3 = userShareD6 === null ? "" : <AnimatedD6 d6={userShareD6} />;
    $[4] = userShareD6;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const userShareDisplay = t3;
  if (stage === "loading") {
    let t4;
    if ($[6] !== t) {
      t4 = t("title");
      $[6] = t;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== amount || $[9] !== formatBalance) {
      t5 = formatBalance(amount);
      $[8] = amount;
      $[9] = formatBalance;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== t) {
      t6 = t("help_aria_label");
      $[11] = t;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== setHelpModalOpen) {
      t7 = () => setHelpModalOpen(true);
      $[13] = setHelpModalOpen;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== boostersContent || $[16] !== className || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
      t8 = <PrizePoolCardLoading className={className} boosters={boostersContent} title={t4} amount={t5} helpAriaLabel={t6} onHelpClick={t7} />;
      $[15] = boostersContent;
      $[16] = className;
      $[17] = t4;
      $[18] = t5;
      $[19] = t6;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  let t4;
  if ($[22] !== stage || $[23] !== t) {
    t4 = stage === "finalized-winner" ? t("congratulations") : t("title");
    $[22] = stage;
    $[23] = t;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  const title = t4;
  let t5;
  if ($[25] !== className) {
    t5 = cn("prize-pool-card relative flex w-full max-w-sm flex-col gap-5 overflow-clip rounded-card border bg-background-primary p-4", className);
    $[25] = className;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== title) {
    t6 = <CardSectionLabel icon={Award01IconSolid}>{title}</CardSectionLabel>;
    $[27] = title;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== amount || $[30] !== formatBalance) {
    t7 = <div className="flex items-center gap-2"><AnimatedNumber value={amount} format={formatBalance} animateOnMount={true} className="text-display-sm font-bold tracking-tight tabular-nums text-foreground-primary" /></div>;
    $[29] = amount;
    $[30] = formatBalance;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== locale || $[33] !== stage || $[34] !== tMarket || $[35] !== userShareDisplay || $[36] !== userSharePct) {
    t8 = stage === "finalized-winner" ? <PoolShareRow label={tMarket("backerRewards.yourShareLabel")} amount={userShareDisplay} sharePercent={formatPercentTrim(userSharePct, 2, locale)} /> : null;
    $[32] = locale;
    $[33] = stage;
    $[34] = tMarket;
    $[35] = userShareDisplay;
    $[36] = userSharePct;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] !== locale || $[39] !== payoutEstimates || $[40] !== payoutEstimatesPending || $[41] !== stage || $[42] !== t) {
    t9 = stage === "pre-finalization" && !payoutEstimatesPending && payoutEstimates !== null && payoutEstimates.length > 0 ? <div className="flex flex-col gap-3 border-border-primary border-t pt-3">{payoutEstimates.map(estimate => <div key={estimate.outcomeIndex} className="flex flex-col gap-0.5"><span className="text-caption text-foreground-secondary">{t("estimated_payout", {
            outcome: estimate.label
          })}</span><span className="flex items-baseline gap-1.5 text-body font-medium text-foreground-primary tabular-nums"><span>{formatClaimantSharePercent(estimate.claimantShareMicroPercent, locale)}</span><span aria-hidden="true" className="text-foreground-tertiary">·</span><AnimatedD6 d6={estimate.estimatedPayoutD6} /></span></div>)}</div> : null;
    $[38] = locale;
    $[39] = payoutEstimates;
    $[40] = payoutEstimatesPending;
    $[41] = stage;
    $[42] = t;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== t6 || $[45] !== t7 || $[46] !== t8 || $[47] !== t9) {
    t10 = <div className="relative flex flex-col gap-3">{t6}{t7}{t8}{t9}</div>;
    $[44] = t6;
    $[45] = t7;
    $[46] = t8;
    $[47] = t9;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== setHelpModalOpen) {
    t11 = () => setHelpModalOpen(true);
    $[49] = setHelpModalOpen;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== t) {
    t12 = t("help_aria_label");
    $[51] = t;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== t11 || $[54] !== t12) {
    t13 = <InfoIconButton onClick={t11} ariaLabel={t12} />;
    $[53] = t11;
    $[54] = t12;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== marketPubkey || $[57] !== marketStatus || $[58] !== stage || $[59] !== t) {
    t14 = stage === "pre-finalization" && canBoostMarket({
      status: marketStatus
    }) ? <BoostIconButton marketPubkey={marketPubkey} ariaLabel={t("boost_aria_label")} /> : null;
    $[56] = marketPubkey;
    $[57] = marketStatus;
    $[58] = stage;
    $[59] = t;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  let t15;
  if ($[61] !== t13 || $[62] !== t14) {
    t15 = <div className="flex items-center gap-3">{t13}{t14}</div>;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== boostersContent || $[65] !== t15) {
    t16 = <div className="relative flex w-full items-end justify-between gap-3">{t15}{boostersContent}</div>;
    $[64] = boostersContent;
    $[65] = t15;
    $[66] = t16;
  } else {
    t16 = $[66];
  }
  let t17;
  if ($[67] !== claimInFlight || $[68] !== claimed || $[69] !== onClaim || $[70] !== stage || $[71] !== tMarket) {
    t17 = stage === "finalized-winner" ? <AnimatedButton variant="primary" size="lg" className="flex-1" onClick={onClaim} disabled={claimed || claimInFlight} loading={claimInFlight}>{claimed ? tMarket("claim.claimed") : tMarket("claim.button")}</AnimatedButton> : null;
    $[67] = claimInFlight;
    $[68] = claimed;
    $[69] = onClaim;
    $[70] = stage;
    $[71] = tMarket;
    $[72] = t17;
  } else {
    t17 = $[72];
  }
  let t18;
  if ($[73] !== t17) {
    t18 = <div className="relative flex empty:hidden">{t17}</div>;
    $[73] = t17;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  let t19;
  if ($[75] !== t10 || $[76] !== t16 || $[77] !== t18 || $[78] !== t5) {
    t19 = <div className={t5}>{t10}{t16}{t18}</div>;
    $[75] = t10;
    $[76] = t16;
    $[77] = t18;
    $[78] = t5;
    $[79] = t19;
  } else {
    t19 = $[79];
  }
  return t19;
}
function PrizePoolCardLoading(t0) {
  const $ = _c(17);
  const {
    className,
    title,
    amount,
    helpAriaLabel,
    onHelpClick,
    boosters
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("prize-pool-card relative flex w-full max-w-sm flex-col gap-5 overflow-clip rounded-card border bg-background-primary p-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <CardSectionLabel icon={Award01IconSolid}>{title}</CardSectionLabel>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== amount) {
    t3 = <span className="text-display-sm font-bold tracking-tight text-foreground-primary tabular-nums">{amount}</span>;
    $[4] = amount;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== helpAriaLabel || $[7] !== onHelpClick) {
    t4 = <InfoIconButton onClick={onHelpClick} ariaLabel={helpAriaLabel} />;
    $[6] = helpAriaLabel;
    $[7] = onHelpClick;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== boosters || $[10] !== t4) {
    t5 = <div className="flex w-full items-end justify-between gap-3">{t4}{boosters}</div>;
    $[9] = boosters;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t5) {
    t6 = <div className={t1} data-testid="prize-pool-viewer-pending">{t2}{t3}{t5}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
const MIN_DISPLAYABLE_MICRO_PERCENT = 10_000;
function formatClaimantSharePercent(shareMicroPercent: number, locale: string): string {
  if (shareMicroPercent === 0) return '0%';
  if (shareMicroPercent < MIN_DISPLAYABLE_MICRO_PERCENT) return '<0.01%';
  return formatPercentTrim(shareMicroPercent / (D6_SCALE * 100), 2, locale);
}

// ---------------------------------------------------------------------------
// Internal pieces — kept local so the card composes one mental model.
// ---------------------------------------------------------------------------

function InfoIconButton(t0) {
  const $ = _c(4);
  const {
    onClick,
    ariaLabel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={HelpCircleIconSolid} className="size-5" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== ariaLabel || $[2] !== onClick) {
    t2 = <AnimatedButton variant="secondary" size="lg" aria-label={ariaLabel} onClick={onClick}>{t1}</AnimatedButton>;
    $[1] = ariaLabel;
    $[2] = onClick;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function BoostIconButton(t0) {
  const $ = _c(13);
  const {
    marketPubkey,
    ariaLabel
  } = t0;
  const [open, setOpen] = useState(false);
  const {
    withAuth
  } = useAuthGate();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== withAuth) {
    t2 = withAuth(t1);
    $[1] = withAuth;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon icon={Rocket01IconSolid} className="size-5" aria-hidden="true" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== ariaLabel || $[5] !== t2) {
    t4 = <AnimatedButton variant="secondary" size="lg" aria-label={ariaLabel} onClick={t2}>{t3}</AnimatedButton>;
    $[4] = ariaLabel;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== marketPubkey || $[8] !== open) {
    t5 = <BoostDialog marketPubkey={marketPubkey} open={open} onOpenChange={setOpen} />;
    $[7] = marketPubkey;
    $[8] = open;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}